import FormGroup from "@/app/shared/form-group";
import { Input } from "rizzui";
import { Textarea } from "rizzui";
import UploadZone from "@/components/ui/file-upload/upload-zone";
import useProfile from "@/hooks/use-profile";
import { Roles } from "@/utilities/types/Users.ts";
import { useFormContext } from "react-hook-form";

export interface AdditionalContentPartProps {
  className?: string;
}

export default function AdditionalContentPart({
  className,
}: AdditionalContentPartProps) {
  const { profile } = useProfile();
  const {
    getValues,
    setValue,
    watch,
    formState: { errors },
    register,
  } = useFormContext();

  return (
    <div className="flex flex-col gap-10">
      <FormGroup title="Hero Image" className={className}>
        <UploadZone
          name="heroImage"
          className="col-span-full upload-zone"
          description="Accept all type of images"
          getValues={getValues}
          setValue={setValue}
          watch={watch}
          error={errors?.heroImage?.message as string}
          inputProps={{
            multiple: false,
            accept: "image/*",
          }}
        />
      </FormGroup>

      <FormGroup title="Hero Content">
        <Input
          label="Catchphrase"
          placeholder="Catchphrase for hero section"
          disabled={profile?.role === Roles.AGENCY}
          {...register("catchphrase")}
          error={errors.catchphrase?.message as string}
        />
        <Input
          label="Headline"
          placeholder="Headline for hero section"
          disabled={profile?.role === Roles.AGENCY}
          {...register("headline")}
          error={errors.headline?.message as string}
          className="col-span-full"
        />
        <Input
          label="Short Description"
          placeholder="Short description for hero section"
          disabled={profile?.role === Roles.AGENCY}
          {...register("shortDescription")}
          error={errors.shortDescription?.message as string}
          className="col-span-full"
        />
      </FormGroup>

      <FormGroup title="Site Plan">
        <Input
          label="Site Plan Title"
          placeholder="Site plan section title"
          disabled={profile?.role === Roles.AGENCY}
          {...register("sitePlanTitle")}
          error={errors.sitePlanTitle?.message as string}
        />
        <Textarea
          label="Site Plan Description"
          placeholder="Site plan section description"
          disabled={profile?.role === Roles.AGENCY}
          {...register("sitePlanDescription")}
          error={errors.sitePlanDescription?.message as string}
          className="col-span-full"
          rows={3}
        />
        <UploadZone
          name="siteplanImages"
          className="col-span-full upload-zone"
          getValues={getValues}
          setValue={setValue}
          watch={watch}
          error={errors?.siteplanImages?.message as string}
          inputProps={{
            multiple: true,
            accept: "image/*",
          }}
        />
      </FormGroup>

      <FormGroup title="Location">
        <Input
          label="Location Title"
          placeholder="Location section title"
          disabled={profile?.role === Roles.AGENCY}
          {...register("locationTitle")}
          error={errors.locationTitle?.message as string}
        />
        <Textarea
          label="Location Description"
          placeholder="Location section description"
          disabled={profile?.role === Roles.AGENCY}
          {...register("amenityHtml")}
          error={errors.amenityHtml?.message as string}
          className="col-span-full"
          rows={3}
        />
        <UploadZone
          name="locationImage"
          className="col-span-full upload-zone"
          getValues={getValues}
          setValue={setValue}
          watch={watch}
          error={errors?.locationImage?.message as string}
          inputProps={{
            multiple: false,
            accept: "image/*",
          }}
        />
      </FormGroup>

      <FormGroup title="Education">
        <Input
          label="Education Title"
          placeholder="Education section title"
          disabled={profile?.role === Roles.AGENCY}
          {...register("educationTitle")}
          error={errors.educationTitle?.message as string}
        />
        <Textarea
          label="Education Description"
          placeholder="Education section description"
          disabled={profile?.role === Roles.AGENCY}
          {...register("educationDescription")}
          error={errors.educationDescription?.message as string}
          className="col-span-full"
          rows={3}
        />
        <UploadZone
          name="educationImage"
          className="col-span-full upload-zone"
          getValues={getValues}
          setValue={setValue}
          watch={watch}
          error={errors?.educationImage?.message as string}
          inputProps={{
            multiple: false,
            accept: "image/*",
          }}
        />
      </FormGroup>

      <FormGroup title="Transport">
        <Input
          label="Transport Title"
          placeholder="Transport section title"
          disabled={profile?.role === Roles.AGENCY}
          {...register("transportTitle")}
          error={errors.transportTitle?.message as string}
        />
        <Textarea
          label="Transport Description"
          placeholder="Transport section description"
          disabled={profile?.role === Roles.AGENCY}
          {...register("transportDescription")}
          error={errors.transportDescription?.message as string}
          className="col-span-full"
          rows={3}
        />
        <UploadZone
          name="transportImage"
          className="col-span-full upload-zone"
          getValues={getValues}
          setValue={setValue}
          watch={watch}
          error={errors?.transportImage?.message as string}
          inputProps={{
            multiple: false,
            accept: "image/*",
          }}
        />
      </FormGroup>

      <FormGroup title="Amenity">
        <Input
          label="Amenity Title"
          placeholder="Amenity section title"
          disabled={profile?.role === Roles.AGENCY}
          {...register("amenityTitle")}
          error={errors.amenityTitle?.message as string}
        />
        <Textarea
          label="Amenity Description"
          placeholder="Amenity section description"
          disabled={profile?.role === Roles.AGENCY}
          {...register("amenityDescription")}
          error={errors.amenityDescription?.message as string}
          className="col-span-full"
          rows={3}
        />
        <UploadZone
          name="amenityImage"
          className="col-span-full upload-zone"
          getValues={getValues}
          setValue={setValue}
          watch={watch}
          error={errors?.amenityImage?.message as string}
          inputProps={{
            multiple: false,
            accept: "image/*",
          }}
        />
      </FormGroup>

      <FormGroup title="SEO Content">
        <Textarea
          label="SEO Text"
          placeholder="SEO text for search engines"
          disabled={profile?.role === Roles.AGENCY}
          {...register("seoText")}
          error={errors.seoText?.message as string}
          className="col-span-full"
          rows={4}
        />
      </FormGroup>

      <FormGroup title="Brochure" description="Accept all type of images">
        <UploadZone
          name="brochureAsset"
          className="col-span-full upload-zone"
          getValues={getValues}
          setValue={setValue}
          watch={watch}
          error={errors?.brochureAsset?.message as string}
          inputProps={{
            multiple: false,
            accept: "image/*",
          }}
        />
      </FormGroup>
    </div>
  );
}
